import { twJoin, twMerge } from "tailwind-merge"
import { Icon } from "../Icon"
import { Heading } from "../Heading"
import { ModalHeaderIconVariation, ModalHeaderProps } from "./types"
import { devWarning } from "../utils/devWarning"
import { Text } from "../Text"

export const iconVariationClassNameMapping: Record<ModalHeaderIconVariation, string> = {
  error: "text-error-base bg-error-lighten-100",
  gray: "text-black-base bg-gray-200",
  info: "text-info-base bg-info-lighten-100",
  primary: "border border-primary-lighten-50 bg-primary-lighten-100",
  success: "text-success-base bg-success-lighten-100",
  warning: "text-warning-base bg-warning-lighten-100",
}

export function ModalHeader({
  align = "start",
  className: classNameProp,
  children,
  icon,
  iconVariation = "primary",
  innerClassName,
  subtitle,
  ...rest
}: ModalHeaderProps) {
  const isCentered = align === "center"

  devWarning(
    Boolean(!isCentered && icon),
    "Icon is only shown when align prop is equal to 'center'",
  )

  const className = twMerge(
    "flex items-center border-b border-b-gray-200 pb-4 px-5",
    align === "start" && "gap-4",
    align === "center" && "flex-col gap-2 text-center",
    classNameProp,
  )

  const _subtitle =
    typeof subtitle === "string" ? <Text className="base-text-small">{subtitle}</Text> : subtitle

  return (
    <div className={className} data-testid="modal-header" {...rest}>
      {icon && isCentered && (
        <div
          aria-hidden
          className={twJoin(
            "grid h-12 w-12 min-w-[48px] place-items-center rounded-full",
            iconVariationClassNameMapping[iconVariation],
          )}
        >
          <Icon name={icon} size="md" />
        </div>
      )}
      <div
        className={twMerge(
          "flex w-full flex-1 flex-col",
          // TODO, handle isCloseable=false here when that feature is added
          isCentered ? "max-w-[calc(100%-2.5rem)]" : "max-w-[calc(100%-1.75rem)]", // This ensures that the text never overlaps the button
        )}
      >
        <Heading size="lg" className={innerClassName}>
          {children}
        </Heading>
        {_subtitle}
      </div>
    </div>
  )
}
